import Plugin from './plugin';
import EventDispatcher from '../event/event-dispatcher';

/**
 * Represents an action of a plugin.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default abstract class Action {
  /**
   * Event called when the action is ready.
   *
   * @type {EventDispatcher<null>}
   * @private
   */
  private _readyEvent = new EventDispatcher<null>();

  /**
   * Whether this action is ready or not.
   *
   * @type {boolean}
   * @private
   */
  private _isReady: boolean = false;

  /**
   * Parent plugin.
   *
   * @type Plugin
   * @private
   */
  private _plugin: Plugin;

  /**
   * Main action container.
   *
   * @type HTMLElement
   * @private
   */
  private _container: HTMLElement;

  /**
   * Main plugin settings.
   *
   * @type { [key: string]: any }
   * @private
   */
  private readonly _settings: { [key: string]: any } = {};

  /**
   * Name of the action.
   *
   * @types {string}
   * @private
   */
  private readonly _name: string;

  /**
   * Language labels.
   *
   * @type { [key: string]: string }
   * @private
   */
  private readonly _labels: { [key: string]: string } = {};


  /**
   * Constructor.
   *
   * @param {Plugin} plugin Parent plugin
   * @param {HTMLElement} container Main action container
   */
  protected constructor(plugin: Plugin, container: HTMLElement) {
    this._plugin = plugin;
    this._container = container;
    this._name = container.dataset.action;

    if (this.container.dataset.settings !== undefined && this.container.dataset.settings) {
      this._settings = JSON.parse(this.container.dataset.settings);
    }

    if (this.container.dataset.labels !== undefined && this.container.dataset.labels) {
      this._labels = JSON.parse(this.container.dataset.labels);
    }
  }


  /**
   * Gets the event that is called when the action is ready.
   *
   * @returns {EventDispatcher<null>}
   */
  get readyEvent(): EventDispatcher<null> {
    return this._readyEvent;
  }


  /**
   * Gets if the plugin is ready or not.
   *
   * @returns {boolean}
   */
  get isReady(): boolean {
    return this._isReady;
  }


  /**
   * Sets if the plugin is ready or not.
   *
   * @param {boolean} isReady Ready state
   */
  set isReady(isReady: boolean) {
    this._isReady = isReady;
  }


  /**
   * Marks the action as ready and notifies the ready event listeners. If the action was already marked as ready before, nothing is done.
   *
   * @returns {void}
   * @protected
   */
  protected markReady(): void {
    if (this.isReady) {
      return;
    }

    this.isReady = true;
    this.readyEvent.dispatch(null);
  }


  /**
   * Gets the parent plugin.
   *
   * @returns {Plugin}
   */
  get plugin(): Plugin {
    return this._plugin;
  }

  /**
   * Sets the parent plugin.
   *
   * @param {Plugin} plugin New parent plugin
   */
  set plugin(plugin: Plugin) {
    this._plugin = plugin;
  }


  /**
   * Gets the main container.
   *
   * @returns {HTMLElement}
   */
  get container(): HTMLElement {
    return this._container;
  }

  /**
   * Sets the main container.
   *
   * @param {HTMLElement} container New main container
   */
  set container(container: HTMLElement) {
    this._container = container;
  }

  /**
   * Gets the name of the action.
   *
   * @returns {string}
   */
  get name(): string {
    return this._name;
  }

  /**
   * Gets the language labels.
   *
   * @returns {{ [key: string]: string }|null}
   */
  get labels(): { [key: string]: string } | null {
    return this._labels;
  }

  /**
   * Gets the main plugin settings.
   *
   * @returns {{ [key: string]: any }}|null
   */
  get settings(): { [key: string]: any } | null {
    return this._settings;
  }
}
